import React, { FC } from 'react';
import { Spin, Space, Row, Col, Typography } from 'antd';

import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { AmountField, Form, FxRateField, TextField } from '~/components';
import AmountInput from '~/components/inputs/AmountInput';
import SendMoneyCard from '~/components/sendMoney/SendMoneyCard';
import { Amount, Consent, SendMoneyState } from '~/types';
import c from '~/utils/constants';
import { isDisplayedEntryCategory } from '~/utils/transaction';

interface Props {
  consent: Consent;
  loading: boolean;
  form: any;
  setMainState: React.Dispatch<React.SetStateAction<SendMoneyState>>;
  disabled?: boolean;
  className?: string;
  currencyFilter?: (currency: string) => boolean;
}

const SendAmount: FC<Props> = (props) => {
  const { consent, loading, setMainState, className, form, disabled, currencyFilter } = props;
  const [t] = useTranslation('sendMoney');
  const tt = (key: string): string => t(`transactions:${key}`);

  const exchangeRate = consent?.details?.currencyConversion?.sender?.rate;

  return (
    <div className={className}>
      <Typography.Title level={4}>{t('translation:amount')}</Typography.Title>
      <SendMoneyCard borderBottomNoRadius={!!consent.totalAmount || loading}>
        <Form
          form={form}
          name="amount"
          onValuesChange={(_, data) => {
            setMainState((prev: any) => ({
              ...prev,
              ...data,
            }));
          }}
        >
          <Form.Item name="amount">
            <AmountInput
              form={form}
              min={0}
              disabled={disabled}
              currencyFilter={currencyFilter}
              onChange={(value: Amount) => {
                setMainState((prev) => ({
                  ...prev,
                  amount: value,
                }));
              }}
            />
          </Form.Item>
        </Form>
      </SendMoneyCard>
      {loading && (
        <div className="SendAmount-Details SendAmount-Details__loading">
          <Spin />
        </div>
      )}
      {consent.totalAmount && !loading && (
        <Space direction="vertical" className="full-width" size={16}>
          <div className="SendAmount-Details">
            <Row>
              <Col span={12}>
                <Space direction="vertical" size={16}>
                  <TextField label={tt('baseAmount')}>
                    <AmountField amount={consent.baseAmount} abs />
                  </TextField>
                  {exchangeRate && (
                    <>
                      <TextField label={tt('exchangeRate')}>
                        <FxRateField
                          baseCurrency={consent?.baseAmount.currency as string}
                          quoteCurrency={consent?.totalAmount.currency as string}
                          rate={exchangeRate}
                        />
                      </TextField>
                      <TextField label={tt('convertedAmount')}>
                        <AmountField amount={consent.amount} abs />
                      </TextField>
                    </>
                  )}
                  {consent?.entries
                    ?.filter((item) => isDisplayedEntryCategory(item.category))
                    ?.map((item, index) => {
                      return (
                        // eslint-disable-next-line react/no-array-index-key
                        <TextField key={index} label={item.description || tt(item.category)}>
                          <AmountField amount={item.amount} abs />
                        </TextField>
                      );
                    })}
                </Space>
              </Col>
              <Col span={12}>
                <TextField label={tt('totalAmount')}>
                  <AmountField className="SendMoney-TotalAmount" amount={consent.totalAmount} abs />
                </TextField>
              </Col>
            </Row>
          </div>
        </Space>
      )}
    </div>
  );
};

export default styled(SendAmount)`
  margin-top: 24px;
  margin-bottom: 24px;
  .ant-form-item {
    margin-bottom: 0;
  }
  .SendAmount-Details {
    background: #ffffff;
    padding: 12px 16px;
    border: 1px solid #e6e6e6;
    border-bottom-left-radius: ${c.borderRadiusBase}px;
    border-bottom-right-radius: ${c.borderRadiusBase}px;
    border-top: none;
    & > .ant-row {
      align-items: baseline;
    }
  }
  .SendAmount-Details__loading {
    display: flex;
    justify-content: center;
    align-items: center;
  }
  .SendAmount-SendButton {
    width: 100%;
    font-size: 18px;
    height: 40px;
    font-weight: 600;
  }
`;
